import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IoClose } from "react-icons/io5";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import {
  applyPermissions,
  SUPER_ADMIN_MENU,
} from "../../../../configs/sidebarMenus.config";
import { useAdminAuth } from "../../../../context/AdminAuthContext";

const AdminSidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, apiLoading: isAuthLoading } = useAdminAuth();

  const [openDropdowns, setOpenDropdowns] = useState({});

  const menuItems = useMemo(() => {
    const permissions = admin?.permissions || [];
    return applyPermissions(SUPER_ADMIN_MENU, permissions);
  }, [admin?.permissions]);

  const toggleSidebar = () => setIsOpen(!isOpen);

  const toggleDropdown = (key) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isRouteActive = (currentPath, targetPath) => {
    if (!targetPath) return false;
    if (currentPath === targetPath) return true;
    return currentPath.startsWith(targetPath + "/");
  };

  const isAnyChildActive = (children = []) =>
    children.some((child) => isRouteActive(location.pathname, child.path));

  // Auto-open dropdowns based on URL
  useEffect(() => {
    const dropdownState = {};

    SUPER_ADMIN_MENU.forEach((item) => {
      if (item.isDropdown && item.dropdownKey) {
        dropdownState[item.dropdownKey] = isAnyChildActive(item.children);
      }
    });

    setOpenDropdowns((prev) => ({ ...prev, ...dropdownState }));
  }, [location.pathname]);
  const renderSkeleton = () => (
    <div className="space-y-3 px-4 pt-2">
      <div className="flex justify-center mb-6">
        <div className="h-14 w-32 bg-gray-200 rounded-lg animate-pulse" />
      </div>
      {[...Array(8)].map((_, i) => (
        <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />
      ))}
    </div>
  );

  const renderMenuItems = () => (
    <ul>
      {menuItems.map((item) => {
        const isDropdown = item.isDropdown;
        const children = item.children || [];

        //  CASE 1: Dropdown with ONLY ONE child → flatten it
        if (isDropdown && children.length === 1) {
          const child = children[0];
          const isActive = isRouteActive(location.pathname, child.path);

          return (
            <li key={child.path}>
              <button
                onClick={() => {
                  navigate(child.path);
                  if (window.innerWidth < 1024) setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 my-1 rounded-xl transition-all
                ${
                  isActive
                    ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow"
                    : "text-primaryTextColor hover:bg-gray-100 hover:text-accentTextColor"
                }`}
              >
                <span className="w-5 flex items-center justify-center shrink-0">
                  <child.icon className="size-4 opacity-80" />
                </span>

                <span className="text-[0.9rem] font-medium truncate">
                  {child.label}
                </span>
              </button>
            </li>
          );
        }

        // CASE 2: Normal dropdown (2+ children)
        if (isDropdown) {
          const isChildActive = isAnyChildActive(children);
          const isActive = isChildActive;
          const isDropdownOpen = openDropdowns[item.dropdownKey];

          return (
            <li key={item.dropdownKey || item.label}>
              <button
                onClick={() => toggleDropdown(item.dropdownKey)}
                className={`w-full flex items-center gap-2 px-3 py-2 my-1 rounded-xl transition-all
                ${
                  isActive
                    ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow-md"
                    : "text-primaryTextColor hover:bg-gray-100"
                }`}
              >
                <span className="w-5 flex items-center justify-center">
                  <item.icon className="size-4 opacity-80" />
                </span>

                <span className="text-[0.9rem] font-medium flex-1 text-left">
                  {item.label}
                </span>

                <MdKeyboardArrowDown
                  size={18}
                  className={`transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="ml-8 mt-1 space-y-1 overflow-hidden"
                  >
                    {children.map((child) => {
                      const isChildRouteActive = isRouteActive(
                        location.pathname,
                        child.path,
                      );

                      return (
                        <li key={child.path}>
                          <button
                            onClick={() => {
                              navigate(child.path);
                              if (window.innerWidth < 1024) setIsOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg transition-all
                            ${
                              isChildRouteActive
                                ? "bg-accentTextColor/20 text-accentTextColor font-semibold"
                                : "text-gray-600 hover:text-accentTextColor hover:bg-gray-50"
                            }`}
                          >
                            <child.icon className="size-3 opacity-80" />
                            <span>{child.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>
          );
        }

        //  CASE 3: Normal single menu item (no dropdown)
        const isActive = isRouteActive(location.pathname, item.path);

        return (
          <li key={item.path || item.key}>
            <button
              onClick={() => {
                navigate(item.path);
                if (window.innerWidth < 1024) setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 my-1 rounded-xl transition-all
              ${
                isActive
                  ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow"
                  : "text-primaryTextColor hover:bg-gray-100 hover:text-accentTextColor"
              }`}
            >
              <span className="w-5 flex items-center justify-center shrink-0">
                <item.icon className="size-4 opacity-80" />
              </span>

              <span className="text-[0.9rem] font-medium truncate">
                {item.label}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );


  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 h-screen w-[14.5rem] z-30">
        <motion.aside
          className="h-screen w-full overflow-y-auto no-scrollbar px-2 pt-6 pb-2
            bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 shadow-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <div
            className="flex justify-center mb-4 cursor-pointer"
            onClick={() => navigate("/super/dashboard")}
          >
            <img src="/logo.png" className="h-14" alt="logo" />
          </div>

          {isAuthLoading ? renderSkeleton() : renderMenuItems()}
        </motion.aside>
      </div>

      {/* Mobile Sidebar */}
      <div className="block lg:hidden">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleSidebar}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="fixed top-0 left-0 h-screen w-60 max-w-[80%] z-50"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="h-screen overflow-y-auto py-6 px-4 bg-gradient-to-b from-white to-gray-50 shadow-2xl border-r border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <img
                    src="/logo.png"
                    className="h-12 cursor-pointer"
                    alt="logo"
                    onClick={() => navigate("/super/dashboard")}
                  />

                  <motion.button
                    onClick={toggleSidebar}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IoClose size={24} />
                  </motion.button>
                </div>

                <motion.nav
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {isAuthLoading ? renderSkeleton() : renderMenuItems()}
                </motion.nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default AdminSidebar;

// import React, { useEffect, useMemo, useState } from "react";
// import { AnimatePresence, motion } from "framer-motion";
// import { IoClose } from "react-icons/io5";
// import { MdKeyboardArrowDown } from "react-icons/md";
// import { useLocation, useNavigate } from "react-router-dom";
// import {
//   applyPermissions,
//   SUPER_ADMIN_MENU,
// } from "../../../../configs/sidebarMenus.config";
// import { useAdminAuth } from "../../../../context/AdminAuthContext";

// const AdminSidebar = ({ isOpen, setIsOpen }) => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { admin, apiLoading: isAuthLoading } = useAdminAuth();

//   const [openDropdowns, setOpenDropdowns] = useState({});

//   const menuItems = useMemo(() => {
//     const permissions = admin?.permissions || [];
//     return applyPermissions(SUPER_ADMIN_MENU, permissions);
//   }, [admin?.permissions]);

//   const toggleSidebar = () => setIsOpen(!isOpen);

//   const toggleDropdown = (key) => {
//     setOpenDropdowns((prev) => ({
//       ...prev,
//       [key]: !prev[key],
//     }));
//   };

//   const isRouteActive = (currentPath, targetPath) => {
//     if (!targetPath) return false;
//     if (currentPath === targetPath) return true;
//     return currentPath.startsWith(targetPath + "/");
//   };

//   const isAnyChildActive = (children = []) =>
//     children.some((child) => isRouteActive(location.pathname, child.path));

//   // Auto-open dropdowns based on URL
//   useEffect(() => {
//     const dropdownState = {};

//     SUPER_ADMIN_MENU.forEach((item) => {
//       if (item.isDropdown && item.dropdownKey) {
//         dropdownState[item.dropdownKey] = isAnyChildActive(item.children);
//       }
//     });

//     setOpenDropdowns((prev) => ({ ...prev, ...dropdownState }));
//   }, [location.pathname]);
//   const renderSkeleton = () => (
//     <div className="space-y-3 px-4 pt-2">
//       <div className="flex justify-center mb-6">
//         <div className="h-14 w-32 bg-gray-200 rounded-lg animate-pulse" />
//       </div>
//       {[...Array(8)].map((_, i) => (
//         <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />
//       ))}
//     </div>
//   );

//   const renderMenuItems = () => (
//     <ul>
//       {menuItems.map((item) => {
//         const isDropdown = item.isDropdown;
//         const isChildActive = isAnyChildActive(item.children);
//         const isActive = isDropdown
//           ? isChildActive
//           : isRouteActive(location.pathname, item.path);

//         const isDropdownOpen = isDropdown && openDropdowns[item.dropdownKey];

//         if (isDropdown) {
//           return (
//             <li key={item.dropdownKey || item.label}>
//               <button
//                 onClick={() => toggleDropdown(item.dropdownKey)}
//                 className={`w-full flex items-center gap-2 px-3 py-2 my-1 rounded-xl transition-all
//                   ${
//                     isActive
//                       ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow-md"
//                       : "text-primaryTextColor hover:bg-gray-100"
//                   }`}
//               >
//                 <span className="w-5 flex items-center justify-center">
//                   <item.icon className="size-4 opacity-80" />
//                 </span>

//                 <span className="text-[0.9rem] font-medium flex-1 text-left">
//                   {item.label}
//                 </span>

//                 <MdKeyboardArrowDown
//                   size={18}
//                   className={`transition-transform duration-200 ${
//                     isDropdownOpen ? "rotate-180" : ""
//                   }`}
//                 />
//               </button>

//               <AnimatePresence>
//                 {isDropdownOpen && (
//                   <motion.ul
//                     initial={{ height: 0, opacity: 0 }}
//                     animate={{ height: "auto", opacity: 1 }}
//                     exit={{ height: 0, opacity: 0 }}
//                     transition={{ duration: 0.2 }}
//                     className="ml-8 mt-1 space-y-1 overflow-hidden"
//                   >
//                     {item.children.map((child) => {
//                       const isChildRouteActive = isRouteActive(
//                         location.pathname,
//                         child.path,
//                       );

//                       return (
//                         <li key={child.path}>
//                           <button
//                             onClick={() => {
//                               navigate(child.path);
//                               if (window.innerWidth < 1024) setIsOpen(false);
//                             }}
//                             className={`w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg transition-all
//                               ${
//                                 isChildRouteActive
//                                   ? "bg-accentTextColor/20 text-accentTextColor font-semibold"
//                                   : "text-gray-600 hover:text-accentTextColor hover:bg-gray-50"
//                               }`}
//                           >
//                             <child.icon className="size-3 opacity-80" />
//                             <span>{child.label}</span>
//                           </button>
//                         </li>
//                       );
//                     })}
//                   </motion.ul>
//                 )}
//               </AnimatePresence>
//             </li>
//           );
//         }

//         return (
//           <li key={item.path || item.key}>
//             <button
//               onClick={() => {
//                 navigate(item.path);
//                 if (window.innerWidth < 1024) setIsOpen(false);
//               }}
//               className={`w-full flex items-center gap-2 px-3 py-2 my-1 rounded-xl transition-all
//                 ${
//                   isActive
//                     ? "bg-gradient-to-r from-brandAccentColor to-accentTextColor text-white shadow"
//                     : "text-primaryTextColor hover:bg-gray-100 hover:text-accentTextColor"
//                 }`}
//             >
//               <span className="w-5 flex items-center justify-center shrink-0">
//                 <item.icon className="size-4 opacity-80" />
//               </span>

//               <span className="text-[0.9rem] font-medium truncate">
//                 {item.label}
//               </span>
//             </button>
//           </li>
//         );
//       })}
//     </ul>
//   );

//   return (
//     <>
//       {/* Desktop Sidebar */}
//       <div className="hidden lg:block fixed left-0 top-0 h-screen w-[14.5rem] z-30">
//         <motion.aside
//           className="h-screen w-full overflow-y-auto no-scrollbar px-2 pt-6 pb-2
//             bg-gradient-to-b from-white to-gray-50 border-r border-gray-200 shadow-lg"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 0.2 }}
//         >
//           <div
//             className="flex justify-center mb-4 cursor-pointer"
//             onClick={() => navigate("/super/dashboard")}
//           >
//             <img src="/logo.png" className="h-14" alt="logo" />
//           </div>

//           {isAuthLoading ? renderSkeleton() : renderMenuItems()}
//         </motion.aside>
//       </div>

//       {/* Mobile Sidebar */}
//       <div className="block lg:hidden">
//         <AnimatePresence>
//           {isOpen && (
//             <motion.div
//               className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               exit={{ opacity: 0 }}
//               onClick={toggleSidebar}
//             />
//           )}
//         </AnimatePresence>

//         <AnimatePresence>
//           {isOpen && (
//             <motion.div
//               className="fixed top-0 left-0 h-screen w-60 max-w-[80%] z-50"
//               initial={{ x: "-100%" }}
//               animate={{ x: 0 }}
//               exit={{ x: "-100%" }}
//               transition={{ type: "spring", damping: 25, stiffness: 300 }}
//             >
//               <div className="h-screen overflow-y-auto py-6 px-4 bg-gradient-to-b from-white to-gray-50 shadow-2xl border-r border-gray-200">
//                 <div className="flex items-center justify-between mb-4">
//                   <img
//                     src="/logo.png"
//                     className="h-12 cursor-pointer"
//                     alt="logo"
//                     onClick={() => navigate("/super/dashboard")}
//                   />

//                   <motion.button
//                     onClick={toggleSidebar}
//                     whileHover={{ scale: 1.1 }}
//                     whileTap={{ scale: 0.9 }}
//                   >
//                     <IoClose size={24} />
//                   </motion.button>
//                 </div>

//                 <motion.nav
//                   initial={{ y: 20, opacity: 0 }}
//                   animate={{ y: 0, opacity: 1 }}
//                   transition={{ delay: 0.2 }}
//                 >
//                   {isAuthLoading ? renderSkeleton() : renderMenuItems()}
//                 </motion.nav>
//               </div>
//             </motion.div>
//           )}
//         </AnimatePresence>
//       </div>
//     </>
//   );
// };

// export default AdminSidebar;
